/**
 * Converts markdown-style links to HTML anchor tags
 * @param instruction - String containing markdown-style links in the format [text](url)
 * @param target - The target attribute specifies where to open the linked document.
 * @returns String with Markdown links converted to HTML <a> tags with dynamic target
 * @example
 * renderMarkdown("Click [here](https://example.com)") 
 * // Returns: 'Click <a href="https://example.com" target="_blank">here</a>'
 */
export const renderMarkdown = (instruction: string, target = "_blank") => {
    return instruction.replace(/\[(.*?)]\((.*?)\)/g, `<a href="$2" target="${target}">$1</a>`);
};

/**
 * Minifies an HTML string by removing excessive whitespace and line breaks.
 *
 * This function replaces all sequences of whitespace characters (including
 * spaces, tabs, and line breaks) with a single space and trims any leading
 * or trailing whitespace.
 *
 * @param {string} html - The HTML string to be minified.
 * @returns {string} - The minified HTML string.
 */
export function minifyHTML(html: string): string {
    // minifys html code, removing linebreaks and trimming whitespaces
    return html.replace(/\s+/g, ' ').trim();
}

/**
 * A piece of parsed markdown: plain text, bold text, or a link. Used by
 * MarkdownRenderer to render real DOM nodes instead of injecting raw HTML with
 * `v-html`.
 */
export type MarkdownSegment =
    | { type: "text"; text: string }
    | { type: "strong"; text: string }
    | { type: "em"; text: string }
    | { type: "link"; text: string; href: string };

// Each pattern matches ONE kind of token, so it is easy to read in isolation.
// Bold accepts both the legacy `<strong>` HTML (frontend i18n strings) and
// CommonMark `**bold**` (server-owned v1 strings) — group 1 = HTML text,
// group 2 = `**` text.
const BOLD_PATTERN = /<strong>(.*?)<\/strong>|\*\*([^*]+?)\*\*/g;
const LINK_PATTERN = /\[([^\]]+)\]\(([^)]+)\)/g; // group 1: label, group 2: url
const EM_PATTERN = /\*([^*]+?)\*/g; // CommonMark `*italic*` (bold `**` is consumed first)

// Schemes we allow inside an href. Anything else (e.g. `javascript:`) is treated
// as unsafe and rendered as plain text instead of a link.
const SAFE_HREF_PATTERN = /^(https?:|mailto:)/i;

/**
 * Returns true when an href is safe to render as a link: site-relative paths and
 * http(s)/mailto URLs are allowed, every other scheme is rejected.
 */
export function isSafeHref(href: string): boolean {
    const value = href.trim();
    return value.startsWith("/") || SAFE_HREF_PATTERN.test(value);
}

interface Chunk {
    /** The plain-text run between matches (empty string for a matched chunk). */
    text: string;
    /** The regex match for this chunk, or null when it is a plain-text run. */
    match: RegExpMatchArray | null;
}

/**
 * Walks `pattern` across `text` and returns its pieces in order: each match and
 * the plain text between the matches. `pattern` must be a global (`/g`) regex.
 */
function splitAround(text: string, pattern: RegExp): Chunk[] {
    const chunks: Chunk[] = [];
    let cursor = 0;

    for (const match of text.matchAll(pattern)) {
        const start = match.index ?? 0;
        if (start > cursor) {
            chunks.push({ text: text.slice(cursor, start), match: null });
        }
        chunks.push({ text: match[0], match });
        cursor = start + match[0].length;
    }

    if (cursor < text.length) {
        chunks.push({ text: text.slice(cursor), match: null });
    }

    return chunks;
}

/**
 * Splits a plain-text run around `*italic*` spans, pushing `em`/`text` segments.
 * Runs last so bold (`**`) and links are already consumed.
 */
function pushItalicsAndText(segments: MarkdownSegment[], run: string): void {
    for (const emChunk of splitAround(run, EM_PATTERN)) {
        if (emChunk.match) {
            segments.push({ type: "em", text: emChunk.match[1] });
        } else if (emChunk.text) {
            segments.push({ type: "text", text: emChunk.text });
        }
    }
}

/**
 * Parses a raw string into safe, typed segments for the small markdown subset we
 * support: bold (`<strong>…</strong>` or `**…**`), italics (`*…*`), and links
 * (`[label](url)`).
 *
 * It works in ordered passes: first split around bold blocks, then split each
 * remaining run around links, then split the leftover plain text around italics.
 * Plain strings with no markup return a single text segment, so callers can
 * render every value the same way.
 */
export function parseMarkdown(input: string): MarkdownSegment[] {
    if (!input) {
        return [];
    }

    const segments: MarkdownSegment[] = [];

    for (const boldChunk of splitAround(input, BOLD_PATTERN)) {
        if (boldChunk.match) {
            // `<strong>` text is group 1; `**bold**` text is group 2.
            segments.push({ type: "strong", text: boldChunk.match[1] ?? boldChunk.match[2] });
            continue;
        }

        // Not bold: look for markdown links inside this plain-text run.
        for (const linkChunk of splitAround(boldChunk.text, LINK_PATTERN)) {
            if (!linkChunk.match) {
                pushItalicsAndText(segments, linkChunk.text);
                continue;
            }

            const [, label, url] = linkChunk.match;
            if (isSafeHref(url)) {
                segments.push({ type: "link", text: label, href: url.trim() });
            } else {
                // Unsafe link: keep the label as text so no wording is lost.
                segments.push({ type: "text", text: label });
            }
        }
    }

    return segments;
}

const STRONG_TAG_PATTERN = /<strong>(.*?)<\/strong>/g;

/**
 * Rewrites legacy `<strong>…</strong>` emphasis as CommonMark `**…**` so copy
 * written for `MarkdownRenderer` can be handed to the design-system markdown
 * (which reads `**bold**`, `*italic*` and `[label](url)`). Links, italics and
 * text already using `**` are left untouched.
 */
export function toDesignSystemMarkdown(text: string): string {
    return text.replace(STRONG_TAG_PATTERN, "**$1**");
}
